import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, Pencil, Plus, Trash2, BookMarked } from "lucide-react";

const OWNER_ROLES = ["cro", "ciso", "cpo", "cfo", "admin"];
const KINDS = ["manual", "notify", "auto_remediation"];

export default function PlaybookLibrary() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/playbooks?scope=library").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/playbooks?scope=org").then(({ data }) => setOrgs(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const clone = async (id) => {
    try {
      const { data } = await api.post(`/playbooks/${id}/clone`);
      toast.success(`Cloned to “${data.title}” — original template hidden`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clone failed");
    }
  };

  const toggleHidden = async (pb) => {
    try {
      await api.post(`/playbooks/${pb.playbook_id}/visibility`, { hidden: !pb.hidden });
      toast.success(pb.hidden ? "Template restored to catalog" : "Template hidden");
      load();
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  const openEditor = (pb) => setEditing({ ...pb, steps: pb.steps.map((s) => ({ ...s })) });

  const saveEditor = async () => {
    setSaving(true);
    try {
      await api.patch(`/playbooks/${editing.playbook_id}`, {
        title: editing.title,
        steps: editing.steps.map((s) => ({
          step_no: s.step_no,
          label: s.label,
          owner_role: s.owner_role,
          kind: s.kind,
          evidence_required: !!s.evidence_required,
        })),
      });
      toast.success("Playbook updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const updateStep = (i, patch) =>
    setEditing((e) => ({ ...e, steps: e.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const addStep = () =>
    setEditing((e) => ({ ...e, steps: [...e.steps, { step_no: e.steps.length + 1, label: "", owner_role: "cro", kind: "manual", evidence_required: false }] }));
  const removeStep = (i) =>
    setEditing((e) => ({ ...e, steps: e.steps.filter((_, j) => j !== i) }));

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="playbook-library-page">
        <p className="ek-eyebrow">Onboarding · governance</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Playbook Library</h1>
        <p className="mt-3 max-w-2xl text-base text-ek-ink/65">
          Best-practice templates you can clone into editable, organisation-specific playbooks.
          Replace the seeded content with your own response runbooks on deployment.
        </p>

        {/* Templates */}
        <h2 className="ek-eyebrow mt-10 flex items-center gap-1"><BookMarked size={12} /> Best-practice templates</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="template-list">
          {templates.map((pb) => (
            <div
              key={pb.playbook_id}
              data-testid={`template-${pb.playbook_id}`}
              className={`border p-5 ${pb.hidden ? "border-dashed border-black/15 bg-black/[0.02] opacity-70" : "border-black/5 bg-white"}`}
            >
              <div className="flex items-start justify-between">
                <h3 className="font-display text-base font-semibold text-ek-ink">{pb.title}</h3>
                {pb.hidden && <span className="shrink-0 bg-ek-canvas px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-ek-ink/65">Hidden</span>}
              </div>
              <p className="mt-1 text-xs text-ek-ink/65">{pb.steps.length} steps · triggers ≥ {pb.trigger.min_residual_score}</p>
              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => clone(pb.playbook_id)}
                  data-testid={`clone-${pb.playbook_id}`}
                  className="flex items-center gap-1 rounded-sm bg-ek-purple px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-ek-purple-deep"
                >
                  <Copy size={12} /> Clone to org
                </button>
                <button
                  onClick={() => toggleHidden(pb)}
                  data-testid={`toggle-hidden-${pb.playbook_id}`}
                  className="flex items-center gap-1 rounded-sm border border-black/10 px-2.5 py-1.5 text-xs font-medium text-ek-ink/60 transition-colors hover:border-ek-ink"
                >
                  {pb.hidden ? <><Eye size={12} /> Restore</> : <><EyeOff size={12} /> Hide</>}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Org playbooks */}
        <h2 className="ek-eyebrow mt-12 flex items-center gap-1"><Pencil size={12} /> Organisation playbooks</h2>
        {orgs.length === 0 ? (
          <p className="mt-3 text-sm text-ek-ink/65">No org playbooks yet — clone a template to create your first editable playbook.</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3" data-testid="org-list">
            {orgs.map((pb) => (
              <div key={pb.playbook_id} data-testid={`org-${pb.playbook_id}`} className="border border-ek-purple/15 bg-white p-5">
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-base font-semibold text-ek-ink">{pb.title}</h3>
                  <span className="shrink-0 bg-ek-purple/10 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase text-ek-purple">Org · v{pb.version}</span>
                </div>
                <p className="mt-1 text-xs text-ek-ink/65">{pb.steps.length} steps</p>
                <button
                  onClick={() => openEditor(pb)}
                  data-testid={`edit-${pb.playbook_id}`}
                  className="mt-4 flex items-center gap-1 rounded-sm border border-ek-purple/30 px-2.5 py-1.5 text-xs font-semibold text-ek-purple transition-colors hover:bg-ek-purple/5"
                >
                  <Pencil size={12} /> Edit
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="playbook-editor">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit playbook</DialogTitle>
            <DialogDescription>Tailor the steps, owners and evidence requirements for your organisation.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-ek-ink">Title</label>
                <input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="editor-title"
                  className="w-full rounded-[4px] border border-black/10 px-3 py-2 text-sm outline-none focus:border-ek-purple focus:ring-2 focus:ring-ek-purple/20"
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium text-ek-ink">Steps</label>
                  <button onClick={addStep} data-testid="editor-add-step" className="flex items-center gap-1 text-xs font-semibold text-ek-purple">
                    <Plus size={12} /> Add step
                  </button>
                </div>
                <div className="space-y-2">
                  {editing.steps.map((s, i) => (
                    <div key={i} className="border border-black/10 bg-ek-canvas/50 p-3" data-testid={`editor-step-${i}`}>
                      <div className="flex items-start gap-2">
                        <span className="mt-2 text-xs font-bold text-ek-ink/65">{i + 1}</span>
                        <input
                          value={s.label}
                          onChange={(e) => updateStep(i, { label: e.target.value })}
                          placeholder="Step description"
                          className="flex-1 rounded-[4px] border border-black/10 px-2 py-1.5 text-sm outline-none focus:border-ek-purple"
                        />
                        <button onClick={() => removeStep(i)} className="mt-1.5 text-ek-ink/30 hover:text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 pl-6">
                        <Select value={s.owner_role} onValueChange={(v) => updateStep(i, { owner_role: v })}>
                          <SelectTrigger className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {OWNER_ROLES.map((r) => <SelectItem key={r} value={r} className="text-xs">{r.toUpperCase()}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Select value={s.kind} onValueChange={(v) => updateStep(i, { kind: v })}>
                          <SelectTrigger className="h-7 w-36 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {KINDS.map((k) => <SelectItem key={k} value={k} className="text-xs">{k.replace("_", " ")}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <label className="flex items-center gap-1 text-xs text-ek-ink/60">
                          <input
                            type="checkbox"
                            checked={!!s.evidence_required}
                            onChange={(e) => updateStep(i, { evidence_required: e.target.checked })}
                          />
                          Evidence
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={saveEditor} disabled={saving} data-testid="editor-save" className="ek-btn w-full disabled:opacity-50">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
